import type { Active, UniqueIdentifier } from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import type { ReactNode } from 'react';
import React, { useMemo, useState } from 'react';
import { SortableOverlay } from './SortableOverlay';
import './styles.css';

interface Props<T> {
  items: T[];
  getId: (item: T) => UniqueIdentifier;
  renderItem(item: T, index: number): ReactNode;
  onMove: (indexA: number, indexB: number) => void;
}

function DndProvider<T>({ items, onMove, getId, renderItem }: Props<T>) {
  const [active, setActive] = useState<Active | null>(null);
  const activeItem = useMemo(
    () => items.find((item) => active && getId(item) === active.id),
    [active, getId, items],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  return (
    <DndContext
      sensors={sensors}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over?.id) {
          const activeIndex = items.findIndex(
            (item) => getId(item) === active.id,
          );
          const overIndex = items.findIndex((item) => getId(item) === over.id);

          onMove(activeIndex, overIndex);
        }
        setActive(null);
      }}
      onDragStart={({ active }) => {
        setActive(active);
      }}
      onDragCancel={() => {
        setActive(null);
      }}
    >
      <SortableContext
        items={items.map((item) => ({ ...item, id: getId(item) }))}
      >
        <ul className='SortableList' role='application'>
          {items.map((item, index) => (
            <React.Fragment key={getId(item)}>
              {renderItem(item, index)}
            </React.Fragment>
          ))}
        </ul>
      </SortableContext>
      <SortableOverlay>
        {activeItem ? renderItem(activeItem, -1) : null}
      </SortableOverlay>
    </DndContext>
  );
}

export default DndProvider;
